import { invokeFunction } from "../api";
import { toApiRequestError } from "../errors";
import type { ViewerContext } from "../../context/AuthContext";

type ViewerRole = ViewerContext["role"];
type UserRole = "superadmin" | "admin" | "editor" | "user";

export interface AdminUserSummary {
  user_id: string;
  email: string;
  role: UserRole;
  created_at: string;
  active: boolean;
  first_name: string | null;
  last_name: string | null;
}

export interface FacilitySummary {
  facility_id: string;
  facility_name: string;
}

export interface UserFacilityAssignment {
  assignment_id: string;
  facility_id: string;
  role: string;
  active: boolean;
}

export interface AdminUserDetailResponse {
  user: AdminUserSummary;
  facility_assignments: UserFacilityAssignment[];
  available_facilities: FacilitySummary[];
}

export interface CreateAdminUserInput {
  email: string;
  role: UserRole;
  temporary_password: string;
}

function asRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function asRole(value: unknown): UserRole {
  if (
    value === "superadmin" ||
    value === "admin" ||
    value === "editor" ||
    value === "user"
  )
    return value;
  return "user";
}

function toUser(value: unknown): AdminUserSummary {
  if (!asRecord(value)) throw new Error("Malformed user in server response");
  if (typeof value.user_id !== "string")
    throw new Error("Malformed user in server response");
  if (typeof value.email !== "string")
    throw new Error("Malformed user in server response");
  return {
    user_id: value.user_id,
    email: value.email,
    role: asRole(value.role),
    created_at:
      typeof value.created_at === "string"
        ? value.created_at
        : new Date(0).toISOString(),
    active: value.active !== false,
    first_name: typeof value.first_name === "string" ? value.first_name : null,
    last_name: typeof value.last_name === "string" ? value.last_name : null,
  };
}

function toAssignments(value: unknown): UserFacilityAssignment[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((row) => {
      if (!asRecord(row)) return null;
      if (typeof row.assignment_id !== "string") return null;
      if (typeof row.facility_id !== "string") return null;
      if (typeof row.role !== "string") return null;
      return {
        assignment_id: row.assignment_id,
        facility_id: row.facility_id,
        role: row.role,
        active: row.active === true,
      };
    })
    .filter((row): row is UserFacilityAssignment => row !== null);
}

function toFacilities(value: unknown): FacilitySummary[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((row) => {
      if (!asRecord(row)) return null;
      if (typeof row.facility_id !== "string") return null;
      if (typeof row.facility_name !== "string") return null;
      return { facility_id: row.facility_id, facility_name: row.facility_name };
    })
    .filter((row): row is FacilitySummary => row !== null);
}

export function canManageAdminWorkspace(role: ViewerRole): boolean {
  return role === "Admin";
}

export function canManageOrganizationAccess(
  viewerContext: ViewerContext | null,
): boolean {
  return viewerContext?.is_superadmin === true;
}

export function hasAccountPermission(
  viewerContext: ViewerContext | null,
  permissions: string[],
): boolean {
  if (!viewerContext) return false;
  const granted = new Set(viewerContext.permissions || []);
  return permissions.some((permission) => granted.has(permission));
}

export function canManageBillingWorkspace(
  viewerContext: ViewerContext | null,
): boolean {
  return (
    viewerContext?.role === "Admin" ||
    hasAccountPermission(viewerContext, [
      "delegated_account_admin",
      "manage_billing",
    ])
  );
}

export function canManageInsuranceWorkspace(
  viewerContext: ViewerContext | null,
): boolean {
  return viewerContext?.role === "Admin";
}

export function canAccessPolicyWorkspace(
  viewerContext: ViewerContext | null,
): boolean {
  return viewerContext !== null;
}

export function canManageCredentialingWorkspace(
  viewerContext: ViewerContext | null,
): boolean {
  return viewerContext?.role === "Admin";
}

export function canManagePatientCommsWorkspace(
  viewerContext: ViewerContext | null,
): boolean {
  return viewerContext?.role === "Admin" || viewerContext?.role === "Staff";
}

export function canManagePatWorkspace(
  viewerContext: ViewerContext | null,
): boolean {
  return viewerContext?.role === "Admin" || viewerContext?.role === "Staff";
}

export function canManageFacilityAccess(
  viewerContext: ViewerContext | null,
): boolean {
  return (
    viewerContext?.role === "Admin" && viewerContext?.is_superadmin !== true
  );
}

export function canManageModuleToggles(
  viewerContext: ViewerContext | null,
): boolean {
  return (
    viewerContext?.is_backend_superadmin === true ||
    hasAccountPermission(viewerContext, [
      "delegated_account_admin",
      "manage_billing",
    ])
  );
}

export function isRoleElevationBlocked(
  viewerRole: ViewerRole,
  currentRole: UserRole,
  nextRole: UserRole,
): boolean {
  if (!canManageAdminWorkspace(viewerRole)) return true;
  if (nextRole === "superadmin") return true;
  if (nextRole === "admin" && currentRole !== "admin") return true;
  return false;
}

export function isFacilityRoleChangeBlocked(nextRole: UserRole): boolean {
  return nextRole === "admin" || nextRole === "superadmin";
}

export function mergeUnmanagedFacilityAssignments(
  existingAssignments: UserFacilityAssignment[],
  managedFacilityIds: string[],
  selectedManagedFacilityIds: string[],
): string[] {
  const managed = new Set(managedFacilityIds);
  const unmanagedFacilityIds = existingAssignments
    .map((assignment) => assignment.facility_id)
    .filter((facilityId) => !managed.has(facilityId));
  return Array.from(
    new Set([...selectedManagedFacilityIds, ...unmanagedFacilityIds]),
  );
}

export async function getAdminUsers(): Promise<AdminUserSummary[]> {
  const { data, error } = await invokeFunction<{ users: unknown }>(
    "admin-users",
    {
      method: "GET",
      path: "/",
    },
  );

  if (error) throw toApiRequestError(error, "Failed to load users");
  if (!data || !Array.isArray(data.users))
    throw new Error("Malformed admin users response from server");
  return data.users.map(toUser);
}

export async function getAdminUser(
  userId: string,
): Promise<AdminUserDetailResponse> {
  const { data, error } = await invokeFunction<unknown>("admin-users", {
    method: "GET",
    path: `/${userId}`,
  });

  if (error) throw toApiRequestError(error, "Failed to load user detail");
  if (!asRecord(data))
    throw new Error("Malformed user detail response from server");

  return {
    user: toUser(data.user),
    facility_assignments: toAssignments(data.facility_assignments),
    available_facilities: toFacilities(data.available_facilities),
  };
}

export async function createAdminUser(
  input: CreateAdminUserInput,
): Promise<AdminUserSummary> {
  const { data, error } = await invokeFunction<unknown>("admin-users", {
    method: "POST",
    path: "/",
    body: input,
  });

  if (error) throw toApiRequestError(error, "Failed to create user");
  return toUser(data);
}

export async function updateAdminUserRole(
  userId: string,
  role: UserRole,
): Promise<AdminUserSummary> {
  const { data, error } = await invokeFunction<unknown>("admin-users", {
    method: "PATCH",
    path: `/${userId}`,
    body: { role },
  });

  if (error) throw toApiRequestError(error, "Failed to update user role");
  return toUser(data);
}

export async function replaceAdminUserFacilities(
  userId: string,
  facilityIds: string[],
): Promise<UserFacilityAssignment[]> {
  const { data, error } = await invokeFunction<unknown>("admin-users", {
    method: "PUT",
    path: `/${userId}/facilities`,
    body: { facility_ids: facilityIds },
  });

  if (error)
    throw toApiRequestError(error, "Failed to update facility assignments");
  if (!asRecord(data))
    throw new Error("Malformed facility assignment response from server");
  return toAssignments(data.facility_assignments);
}

export function isFieldSurveyor(vc: ViewerContext | null): boolean {
  return vc?.role === "FieldSurveyor";
}
